import { useState, type CSSProperties, type MouseEventHandler } from 'react';

const HIDDEN_TOOLTIP_CONTENT_STYLE: CSSProperties = { display: 'none' };

const TOOLTIP_DELAY_MS = 500;

type UseToggleGroupTooltipsParams = {
  /** Called after tooltip warm/hover state is reset when the pointer leaves the toggle group. */
  onGroupMouseLeave?: MouseEventHandler<HTMLDivElement>;
};

/**
 * Manages sibling tooltip behavior for ToggleGroup.
 *
 * After the first tooltip opens within the group, sibling tooltips open and close
 * immediately so moving between options feels responsive. Leaving the group resets
 * back to the default open delay.
 *
 * Stale sibling tooltip content is hidden via `getContentStyle` because the shared
 * popover still runs its exit transition after close.
 *
 * @returns Tooltip delay, group/option event handlers, and content style lookup for each option.
 */
export const useToggleGroupTooltips = <TValue extends string>({
  onGroupMouseLeave,
}: UseToggleGroupTooltipsParams = {}) => {
  const [isTooltipWarm, setIsTooltipWarm] = useState(false);
  const [hoveredTooltipValue, setHoveredTooltipValue] = useState<TValue | undefined>();
  const tooltipDelay = isTooltipWarm ? 0 : TOOLTIP_DELAY_MS;

  const resetTooltipState = () => {
    setIsTooltipWarm(false);
    setHoveredTooltipValue(undefined);
  };

  const handleGroupMouseLeave: MouseEventHandler<HTMLDivElement> = event => {
    resetTooltipState();
    onGroupMouseLeave?.(event);
  };

  const handleOpen = () => {
    setIsTooltipWarm(true);
  };

  const handleOptionMouseEnter = (optionValue: TValue) => {
    setHoveredTooltipValue(optionValue);
  };

  const getContentStyle = (optionValue: TValue): CSSProperties | undefined =>
    hoveredTooltipValue != null && hoveredTooltipValue !== optionValue ? HIDDEN_TOOLTIP_CONTENT_STYLE : undefined;

  return {
    tooltipDelay,
    handleGroupMouseLeave,
    handleOpen,
    handleOptionMouseEnter,
    getContentStyle,
  };
};
